import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useLocale } from '@/hooks/useLocale';
import { apiErrorMessage } from '@/services/apiClient';
import { PrintDocument } from './PrintDocument';
import { printPageRule } from './printLayout';
import { usePrintableQuery } from '../api/printing.api';

/**
 * A document at a URL, outside the application shell.
 *
 * Not a print stylesheet bolted onto a screen: the existing invoice page prints its own sidebar,
 * and no amount of `@media print` makes an app chrome into a receipt. Opened in a new tab so the
 * counter never blinks, it prints itself once the data is in and closes when the dialog is done.
 *
 * Paper, margins and layout come from the tenant's print template and nowhere else. The old
 * `?format=` parameter is deprecated and ignored: an address cannot override the template.
 */
export function PrintPage({ kind }: { kind: 'receipt' | 'invoice' }) {
  const { t } = useTranslation();
  const { direction } = useLocale();
  const { orderId = '', invoiceId = '' } = useParams<{ orderId: string; invoiceId: string }>();

  const { data, isPending, isError, error } = usePrintableQuery(
    kind,
    kind === 'receipt' ? orderId : invoiceId,
  );

  useEffect(() => {
    if (!data) return;

    // One frame so the QR image has painted before the dialog captures the page.
    const timer = window.setTimeout(() => window.print(), 250);
    const onAfterPrint = () => window.close();

    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, [data]);

  if (isPending) return <p className="print-status">{t('common.loading')}</p>;
  if (isError || !data) {
    return <p className="print-status">{apiErrorMessage(error) ?? t('sales.print.error')}</p>;
  }

  return (
    <main dir={direction} className="print-root" data-testid="print-root">
      <style>{printPageRule(data.template)}</style>
      <PrintDocument document={data} />
    </main>
  );
}
